import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, Button, Badge, Row, Col, Form, Modal, Table } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import FeatherIcon from 'feather-icons-react';
import { API } from '../../config/api';

const http = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['x-auth-token'] = token;
  return config;
});

const fmtM = n => `${Math.round(Number(n) || 0).toLocaleString()}m`;

// "2 pallets × 60 coils @100m (6,000m each)" — handles multiple plan rows.
// overrideCount: when the whole plan is a single row, show the pallets actually
// packable NOW (e.g. part of the order came from stock) instead of the full plan.
function palletPlanText(palletPlan = [], overrideCount = null) {
  if (!Array.isArray(palletPlan) || !palletPlan.length) return '';
  return palletPlan
    .map(pp => {
      const cpp = Number(pp.coilsPerPallet) || 0;
      const cl = Number(pp.coilLength) || 0;
      const pc =
        overrideCount != null && palletPlan.length === 1
          ? overrideCount
          : Number(pp.palletCount) || 0;
      const mpp = pp.metersPerPallet || cpp * cl;
      return `${pc} pallet${pc === 1 ? '' : 's'} × ${cpp} coils${cl ? ` @${cl}m` : ''}${mpp ? ` (${fmtM(mpp)} each)` : ''}`;
    })
    .join(' + ');
}

// Expand an order's pallet plan into ready-to-create pallets, drawing the
// finished coils down across tokens. Each planned pallet pulls metersPerPallet
// (coilsPerPallet × coilLength) from the available coil tokens; coil counts use
// each token's ACTUAL coil length (lengths can vary, tokens can be multiple).
function buildPalletsFromPlan(s) {
  const plan = Array.isArray(s.palletPlan) ? s.palletPlan : [];
  const avail = (s.finishedCoils || []).map(c => ({
    tokenId: c.tokenId,
    jobId: c.jobId || '',
    productLabel: c.productLabel,
    color: c.color,
    coilLength: Number(c.coilLength) || 0,
    left: Number(c.availableLength) || 0
  }));

  const pallets = [];
  plan.forEach(row => {
    const cpp = Number(row.coilsPerPallet) || 0;
    const cl = Number(row.coilLength) || 0;
    const count = Number(row.palletCount) || 0;
    const perPalletMeters = cpp * cl;
    if (perPalletMeters <= 0 || count <= 0) return;

    for (let i = 0; i < count; i++) {
      let needM = perPalletMeters;
      const items = [];
      for (const a of avail) {
        if (needM <= 0.001) break;
        if (a.left <= 0) continue;
        const take = Math.min(needM, a.left);
        const coils = a.coilLength > 0 ? Math.round(take / a.coilLength) : 0;
        items.push({
          tokenId: a.tokenId,
          jobId: a.jobId || '',
          productLabel: a.productLabel,
          color: a.color,
          assignedLength: Math.round(take),
          coilCount: coils,
          coilLength: a.coilLength
        });
        a.left -= take;
        needM -= take;
      }
      if (items.length) {
        pallets.push({
          items,
          customerName: s.client || '',
          notes: `Auto · pallet ${i + 1}/${count}`
        });
      }
    }
  });
  return pallets;
}

const COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32',
  GREY: '#808080', BLACK: '#333', WHITE: '#EEE', RED: '#EF4444',
  YELLOW: '#F59E0B', GREEN: '#22C55E', ORANGE: '#F97316'
};

function useUnpackedCoils() {
  return useQuery({
    queryKey: ['packing', 'coils'],
    queryFn: async () => { const r = await http.get('/api/v1/packing/coils'); return r.data?.data || []; },
    refetchInterval: 10000
  });
}

function useSuggestions() {
  return useQuery({
    queryKey: ['packing', 'suggestions'],
    queryFn: async () => { const r = await http.get('/api/v1/packing/suggestions'); return r.data?.data || []; },
    refetchInterval: 30000
  });
}

function usePallets() {
  return useQuery({
    queryKey: ['packing', 'pallets'],
    queryFn: async () => { const r = await http.get('/api/v1/packing/pallets'); return r.data?.data || []; },
    refetchInterval: 10000
  });
}

export default function PackingStation() {
  const queryClient = useQueryClient();
  const { data: coils = [] } = useUnpackedCoils();
  const { data: pallets = [] } = usePallets();
  const { data: suggestions = [] } = useSuggestions();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [palletTab, setPalletTab] = useState('all');

  const filtered = palletTab === 'all' ? pallets : pallets.filter(p => p.status === palletTab);

  // group the order pack-tasks by client (a client may have several orders/products)
  const byClient = useMemo(() => {
    const m = {};
    for (const s of suggestions) {
      const key = s.client || '— (no client)';
      if (!m[key]) m[key] = [];
      m[key].push(s);
    }
    return m;
  }, [suggestions]);

  // tokenId -> jobId from the coils list (fallback so pallet items always carry
  // jobId, which the PackingPallet schema requires)
  const coilJobById = useMemo(() => {
    const m = {};
    for (const c of coils) if (c.tokenId) m[c.tokenId] = c.jobId || '';
    return m;
  }, [coils]);

  const openPack = useCallback(s => {
    const base = {
      customerName: s.client || '',
      orderCode: s.orderCode || '',
      orderId: s.orderId || ''
    };
    const sEnriched = {
      ...s,
      finishedCoils: (s.finishedCoils || []).map(c => ({
        ...c,
        jobId: c.jobId || coilJobById[c.tokenId] || ''
      }))
    };
    if (sEnriched.hasPalletPlan) {
      // open the modal pre-split into the planned pallets (e.g. 2 × 6000m)
      setPrefill({ ...base, splitPallets: buildPalletsFromPlan(sEnriched) });
    } else {
      // no plan -> open the modal with all of the order's coils as one pallet
      setPrefill({
        ...base,
        items: sEnriched.finishedCoils.map(c => ({
          tokenId: c.tokenId,
          jobId: c.jobId || '',
          productLabel: c.productLabel,
          color: c.color,
          assignedLength: Math.round(Number(c.availableLength) || 0),
          coilCount: Number(c.coilCount) || 0,
          coilLength: Number(c.coilLength) || 0
        }))
      });
    }
    setShowCreateModal(true);
  }, [coilJobById]);

  const closeModal = useCallback(() => {
    setShowCreateModal(false);
    setPrefill(null);
  }, []);

  const doneMutation = useMutation({
    mutationFn: async (code) => { await http.post(`/api/v1/packing/pallets/${code}/done`, {}); },
    onSuccess: () => queryClient.invalidateQueries(['packing'])
  });

  const deleteMutation = useMutation({
    mutationFn: async (code) => { await http.delete(`/api/v1/packing/pallets/${code}`); },
    onSuccess: () => queryClient.invalidateQueries(['packing'])
  });

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-bold">
            <FeatherIcon icon="package" size={22} className="me-2" />
            Packing Station
          </h4>
          <span className="text-muted">Group coils into europallets for warehouse</span>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <FeatherIcon icon="plus" size={16} className="me-1" />
          Create Pallet
        </Button>
      </div>

      <Row>
        {/* LEFT: Unpacked Coils */}
        <Col lg={4}>
          <Card className="mb-3">
            <Card.Header className="py-2 bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">
                  <FeatherIcon icon="disc" size={16} className="me-1" />
                  Unpacked Coils
                </span>
                <Badge bg="secondary" pill className="fs-9">{coils.length}</Badge>
              </div>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
              {coils.length === 0 ? (
                <div className="text-center text-muted py-4">No unpacked coils available</div>
              ) : (
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-end">Remaining</th>
                      <th>Color</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coils.map(c => (
                      <tr key={c.tokenId}>
                        <td>
                          <div className="fw-semibold">{c.productLabel}</div>
                          <small className="text-muted">{c.tokenId?.slice(0, 14)}</small>
                        </td>
                        <td className="text-end">
                          <span className="fw-bold text-success">{c.remainingLength?.toFixed(0)}m</span>
                          {c.assignedLength > 0 && (
                            <div>
                              <small className="text-warning">Assigned: {c.assignedLength?.toFixed(0)}m</small>
                            </div>
                          )}
                        </td>
                        <td>
                          {c.color && (
                            <span className="d-flex align-items-center gap-1">
                              <span style={{ width: 12, height: 12, borderRadius: 3, background: COLOR_HEX[c.color] || '#888', display: 'inline-block' }} />
                              {c.color}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Orders to pack — grouped by client */}
          {suggestions.length > 0 && (
            <Card className="mt-3">
              <Card.Header className="py-2 bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">
                    <FeatherIcon icon="users" size={16} className="me-1 text-primary" />
                    Orders to pack
                  </span>
                  <Badge bg="primary" pill>{suggestions.length}</Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0" style={{ maxHeight: 420, overflow: 'auto' }}>
                {Object.entries(byClient).map(([client, list]) => (
                  <div key={client}>
                    <div className="px-2 py-1 bg-body-tertiary fw-bold border-bottom d-flex justify-content-between">
                      <span>
                        <FeatherIcon icon="user" size={13} className="me-1" />
                        {client}
                      </span>
                      <span className="text-muted fw-normal fs-9">
                        {list.length} product{list.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {list.map((s, i) => (
                      <div key={i} className="border-bottom p-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fw-bold">{s.productLabel}</div>
                            <small className="text-muted">
                              {s.orderCode || s.orderId?.slice(0, 10)}
                              {s.dueDate ? ` · due ${String(s.dueDate).slice(0, 10)}` : ''}
                            </small>
                          </div>
                          <Button
                            size="sm"
                            variant="outline-success"
                            disabled={!(s.finishedCoils || []).length}
                            onClick={() => openPack(s)}
                            title={
                              s.hasPalletPlan
                                ? `Open the ${s.packablePallets ?? s.plannedPallets} pallet(s) packable now, pre-split from this order's coils`
                                : "Build a pallet from this order's coils"
                            }
                          >
                            <FeatherIcon icon="package" size={12} className="me-1" />
                            {s.hasPalletPlan
                              ? (() => {
                                  const pp = s.packablePallets ?? s.plannedPallets;
                                  return `Pack ${pp} pallet${pp === 1 ? '' : 's'}`;
                                })()
                              : 'Pack'}
                          </Button>
                        </div>

                        <div className="mt-1">
                          {s.hasPalletPlan ? (
                            <Badge bg="info" className="me-1">
                              {palletPlanText(s.palletPlan, s.packablePallets)}
                            </Badge>
                          ) : (
                            <Badge bg="secondary" className="me-1">
                              No pallet plan set
                            </Badge>
                          )}
                        </div>

                        <div className="d-flex gap-2 mt-1 flex-wrap">
                          <Badge bg={(s.finishedCoils || []).length ? 'success' : 'secondary'}>
                            {s.totalAvailableCoils} coils ready · {fmtM(s.totalAvailableLength)}
                          </Badge>
                          <small className="text-muted">
                            of {fmtM(s.requiredMeters)} ordered
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}
        </Col>

        {/* RIGHT: Pallets */}
        <Col lg={8}>
          <Card>
            <Card.Header className="py-2 bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold">
                  <FeatherIcon icon="grid" size={16} className="me-1" />
                  Pallets
                </span>
                <div className="d-flex gap-1">
                  {['all', 'draft', 'in_progress', 'done'].map(t => (
                    <Button key={t} size="sm" variant={palletTab === t ? 'primary' : 'outline-secondary'}
                      onClick={() => setPalletTab(t)}>
                      {t === 'all' ? 'All' : t === 'in_progress' ? 'In Progress' : t.charAt(0).toUpperCase() + t.slice(1)}
                      {t !== 'all' && ` (${pallets.filter(p => p.status === t).length})`}
                    </Button>
                  ))}
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
              {filtered.length === 0 ? (
                <div className="text-center text-muted py-4">No pallets</div>
              ) : (
                filtered.map(p => (
                  <PalletCard key={p.code} pallet={p}
                    onDone={() => doneMutation.mutate(p.code)}
                    onDelete={() => deleteMutation.mutate(p.code)}
                  />
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <CreatePalletModal
        show={showCreateModal}
        onHide={closeModal}
        coils={coils}
        prefill={prefill}
        onCreated={() => {
          closeModal();
          queryClient.invalidateQueries(['packing']);
        }}
      />
    </div>
  );
}

// ─── PALLET CARD ───────────────────────────────────────────

function PalletCard({ pallet, onDone, onDelete }) {
  const statusColor = { draft: 'warning', in_progress: 'primary', done: 'success' };

  return (
    <div className="border-bottom p-3">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <span className="fw-bold fs-5">{pallet.code}</span>
          <Badge bg={statusColor[pallet.status] || 'secondary'} className="ms-2 text-uppercase">
            {pallet.status === 'in_progress' ? 'In Progress' : pallet.status}
          </Badge>
          {pallet.customerName && <span className="text-muted ms-2">Client: <strong>{pallet.customerName}</strong></span>}
        </div>
        <div className="d-flex gap-2">
          {pallet.status !== 'done' && (
            <>
              <Button variant="success" onClick={onDone}>
                <FeatherIcon icon="check" size={14} className="me-1" />
                Mark Done
              </Button>
              <Button variant="outline-danger" onClick={onDelete}>
                <FeatherIcon icon="trash-2" size={14} />
              </Button>
            </>
          )}
        </div>
      </div>

      <Table className="mb-2">
        <thead>
          <tr className="text-muted">
            <th>Product</th>
            <th>Color</th>
            <th className="text-end">Length</th>
            <th className="text-end">Coils</th>
            <th className="text-end">Weight</th>
          </tr>
        </thead>
        <tbody>
          {pallet.items.map((item, i) => (
            <tr key={i}>
              <td className="fw-semibold">{item.productLabel}</td>
              <td>
                {item.color && (
                  <span className="d-flex align-items-center gap-1">
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: COLOR_HEX[item.color] || '#888', display: 'inline-block' }} />
                    {item.color}
                  </span>
                )}
              </td>
              <td className="text-end fw-bold">{item.assignedLength}m</td>
              <td className="text-end">{item.coilCount || '—'}</td>
              <td className="text-end">{item.weightKg}kg</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex gap-4 text-muted">
        <span>Total: <strong className="text-dark">{pallet.totalLengthM}m</strong></span>
        <span>Weight: <strong className="text-dark">{pallet.totalWeightKg}kg</strong></span>
        <span>Coils: <strong className="text-dark">{pallet.totalCoils}</strong></span>
        {pallet.completedAt && <span>Completed: <strong>{new Date(pallet.completedAt).toLocaleString()}</strong></span>}
      </div>
      {pallet.notes && <div className="text-muted mt-1"><small>Notes: {pallet.notes}</small></div>}
    </div>
  );
}

// ─── CREATE PALLET MODAL ───────────────────────────────────

function CreatePalletModal({ show, onHide, coils, onCreated, prefill }) {
  const [items, setItems] = useState([]);
  const [splitPallets, setSplitPallets] = useState([]); // pre-split pallets to create
  const [customerName, setCustomerName] = useState('');
  const [orderCode, setOrderCode] = useState('');
  const [orderId, setOrderId] = useState('');
  const [notes, setNotes] = useState('');
  const [coilsPerPallet, setCoilsPerPallet] = useState(50);
  const [error, setError] = useState('');

  // pre-fill from an "Orders to pack" entry:
  //  - splitPallets -> review the N planned pallets, create all
  //  - items        -> single-pallet builder pre-filled with the order's coils
  useEffect(() => {
    if (!show) return;
    if (prefill && Array.isArray(prefill.splitPallets) && prefill.splitPallets.length) {
      setSplitPallets(prefill.splitPallets);
      setItems([]);
      setCustomerName(prefill.customerName || '');
      setOrderCode(prefill.orderCode || '');
      setOrderId(prefill.orderId || '');
      setNotes('');
      setError('');
    } else if (prefill) {
      setItems(Array.isArray(prefill.items) ? prefill.items : []);
      setSplitPallets([]);
      setCustomerName(prefill.customerName || '');
      setOrderCode(prefill.orderCode || '');
      setOrderId(prefill.orderId || '');
      setNotes(prefill.note || '');
      setError('');
    } else {
      // manual "+ Create Pallet" — don't carry a previous split over
      setSplitPallets([]);
    }
  }, [show, prefill]);

  const isSplit = splitPallets.length > 0;

  /**
   * Add a coil batch to the pallet being built.
   *
   * Adding the same token twice makes two rows that are one quantity written
   * out in two halves — same cable, same lot, same everything. The pallet that
   * came out of it read "FY 1x4 (+1)" downstream and listed its token twice,
   * and there was never a reason to want it: two pallets is what "Split into
   * pallets" is for. So the second add tops the first one up instead.
   */
  const addCoil = useCallback((coil) => {
    setItems(prev => {
      const at = prev.findIndex(i => i.tokenId === coil.tokenId);
      if (at !== -1) {
        return prev.map((i, k) =>
          k !== at
            ? i
            : {
                ...i,
                assignedLength: Number(coil.remainingLength) || i.assignedLength,
                coilCount:
                  Number(coil.remainingCoils || coil.coilCount) || i.coilCount
              }
        );
      }
      return [...prev, {
      tokenId: coil.tokenId,
      jobId: coil.jobId,
      productLabel: coil.productLabel,
      color: coil.color,
      section: coil.section || 0,
      numberOfConductors: coil.numberOfConductors || 0,
      assignedLength: coil.remainingLength || 0,
      // prefill coil geometry from the token so "Split into pallets" works
      coilCount: coil.remainingCoils || coil.coilCount || 0,
      coilLength: coil.coilLength || 0,
      machineId: null,
      }];
    });
  }, []);

  const updateItem = useCallback((idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }, []);

  const removeItem = useCallback((idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSubmit = async () => {
    setError('');
    try {
      if (isSplit) {
        for (const pal of splitPallets) {
          await http.post('/api/v1/packing/pallets', {
            items: pal.items,
            customerName,
            orderCode: orderCode || null,
            orderId: orderId || null,
            notes: pal.notes || notes
          });
        }
      } else {
        if (!items.length) { setError('Add at least one coil'); return; }
        await http.post('/api/v1/packing/pallets', {
          items,
          customerName,
          orderCode: orderCode || null,
          orderId: orderId || null,
          notes
        });
      }
      setItems([]);
      setSplitPallets([]);
      setCustomerName('');
      setNotes('');
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  // Split the coils currently in the builder into SEPARATE pallets of
  // `coilsPerPallet` coils each (e.g. 100 coils → 2 pallets of 50). Switches the
  // modal to the split-review mode so "Create N pallets" makes N real pallets —
  // fixes stock coils all landing in one pallet with multiple items.
  const splitIntoPallets = () => {
    setError('');
    const cpp = Number(coilsPerPallet) || 0;
    if (cpp <= 0) { setError('Enter coils per pallet'); return; }
    if (!items.length) { setError('Add at least one coil'); return; }

    const pool = items.map(it => {
      const cl = Number(it.coilLength) || 0;
      let coils = Number(it.coilCount) || 0;
      if (!coils && cl > 0) coils = Math.round((Number(it.assignedLength) || 0) / cl);
      return {
        tokenId: it.tokenId, jobId: it.jobId, productLabel: it.productLabel,
        color: it.color, coilLength: cl || 100, coilsLeft: coils
      };
    });
    let total = pool.reduce((s, p) => s + p.coilsLeft, 0);
    if (total <= 0) { setError('Set coil count / length-per-coil on the items first'); return; }

    const pallets = [];
    while (total > 0 && pallets.length < 500) {
      let need = Math.min(cpp, total);
      const palItems = [];
      for (const a of pool) {
        if (need <= 0) break;
        if (a.coilsLeft <= 0) continue;
        const take = Math.min(need, a.coilsLeft);
        palItems.push({
          tokenId: a.tokenId, jobId: a.jobId, productLabel: a.productLabel, color: a.color,
          coilCount: take, coilLength: a.coilLength, assignedLength: take * a.coilLength
        });
        a.coilsLeft -= take; need -= take; total -= take;
      }
      if (!palItems.length) break;
      pallets.push({ items: palItems, notes: `Stock pallet ${pallets.length + 1}` });
    }
    setSplitPallets(pallets);
  };

  // Calculate total for already added items
  const totalAssigned = items.reduce((s, i) => s + Number(i.assignedLength || 0), 0);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Pallet — Planner</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2">{error}</div>}

        {isSplit ? (
          <div>
            <p className="text-muted mb-2">
              {splitPallets.length} pallet{splitPallets.length === 1 ? '' : 's'} pre-split
              from this order's coils — review and create. The coils leave the
              available list; deleting a pallet returns them.
            </p>
            {splitPallets.map((pal, pi) => {
              const totM = pal.items.reduce((a, it) => a + Number(it.assignedLength || 0), 0);
              const totC = pal.items.reduce((a, it) => a + Number(it.coilCount || 0), 0);
              return (
                <div key={pi} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <strong>Pallet {pi + 1}</strong>
                    <span className="text-muted">{totC} coils · {totM.toLocaleString()}m</span>
                  </div>
                  {pal.items.map((it, ii) => (
                    <div key={ii} className="d-flex justify-content-between align-items-center small">
                      <span className="d-flex align-items-center gap-1">
                        {it.color && (
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: COLOR_HEX[it.color] || '#888', display: 'inline-block' }} />
                        )}
                        {it.productLabel}
                      </span>
                      <span>
                        {it.coilCount} coils{it.coilLength ? ` @${it.coilLength}m` : ''} ={' '}
                        {Number(it.assignedLength).toLocaleString()}m
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
            <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>Customer</Form.Label>
            <Form.Control
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              placeholder="Client name"
            />
          </div>
        ) : (
        <Row>
          {/* Available coils */}
          <Col md={5}>
            <h6 className="text-muted text-uppercase mb-2">Available Coils</h6>
            <div style={{ maxHeight: 350, overflow: 'auto' }}>
              {coils.length === 0 ? (
                <div className="text-center text-muted py-3">No coils available</div>
              ) : coils.map(c => (
                <div key={c.tokenId} className="d-flex justify-content-between align-items-center border-bottom py-2 px-2">
                  <div>
                    <div className="fw-semibold">{c.productLabel}</div>
                    <div className="d-flex align-items-center gap-2">
                      {c.color && <span style={{ width: 10, height: 10, borderRadius: 2, background: COLOR_HEX[c.color] || '#888', display: 'inline-block' }} />}
                      <span className="text-success fw-bold">{c.remainingLength?.toFixed(0)}m</span>
                      <small className="text-muted">remaining</small>
                    </div>
                  </div>
                  <Button size="sm" variant="outline-primary" onClick={() => addCoil(c)}>
                    + Add
                  </Button>
                </div>
              ))}
            </div>
          </Col>

          {/* Pallet contents */}
          <Col md={7}>
            <h6 className="text-muted text-uppercase mb-2">
              Pallet Contents
              {items.length > 0 && <Badge bg="info" className="ms-2">{totalAssigned}m total</Badge>}
            </h6>
            {items.length === 0 ? (
              <div className="text-center text-muted py-4 border rounded">
                Add coils from the left panel
              </div>
            ) : (
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                {items.map((item, i) => (
                  <div key={i} className="border rounded p-2 mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <span className="fw-bold">{item.productLabel}</span>
                        {item.color && (
                          <span className="ms-2 d-inline-flex align-items-center gap-1">
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: COLOR_HEX[item.color] || '#888', display: 'inline-block' }} />
                            <small>{item.color}</small>
                          </span>
                        )}
                      </div>
                      <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removeItem(i)}>
                        <FeatherIcon icon="x" size={16} />
                      </Button>
                    </div>
                    <Row className="g-2">
                      <Col>
                        <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>Total Length (m)</Form.Label>
                        <Form.Control type="number" placeholder="5000"
                          value={item.assignedLength}
                          onChange={e => updateItem(i, 'assignedLength', Number(e.target.value))} />
                      </Col>
                      <Col>
                        <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>Coil Count</Form.Label>
                        <Form.Control type="number" placeholder="100"
                          value={item.coilCount}
                          onChange={e => updateItem(i, 'coilCount', Number(e.target.value))} />
                      </Col>
                      <Col>
                        <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>Length / Coil (m)</Form.Label>
                        <Form.Control type="number" placeholder="50"
                          value={item.coilLength}
                          onChange={e => updateItem(i, 'coilLength', Number(e.target.value))} />
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            )}

            <hr className="my-2" />
            <Row className="g-2">
              <Col>
                <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>Customer (optional)</Form.Label>
                <Form.Control value={customerName} onChange={e => setCustomerName(e.target.value)}
                  placeholder="Client name" />
              </Col>
              <Col>
                <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>Notes</Form.Label>
                <Form.Control value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" />
              </Col>
            </Row>

            {/* Split the added coils into SEPARATE pallets (e.g. 100 coils → 2×50) */}
            <Row className="g-2 mt-2 align-items-end">
              <Col xs="auto">
                <Form.Label className="mb-0 text-muted" style={{ fontSize: 11 }}>Coils / pallet</Form.Label>
                <Form.Control type="number" style={{ width: 120 }} value={coilsPerPallet}
                  onChange={e => setCoilsPerPallet(Number(e.target.value))} />
              </Col>
              <Col xs="auto">
                <Button variant="outline-primary" onClick={splitIntoPallets} disabled={!items.length}>
                  <FeatherIcon icon="grid" size={14} className="me-1" />
                  Split into pallets
                </Button>
              </Col>
              <Col className="text-muted small align-self-center">
                Splits into separate pallets of {coilsPerPallet || '?'} coils each. Or just
                "Create Pallet" to make one.
              </Col>
            </Row>
          </Col>
        </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit}>
          <FeatherIcon icon="check" size={16} className="me-1" />
          {isSplit
            ? `Create ${splitPallets.length} pallet${splitPallets.length === 1 ? '' : 's'}`
            : 'Create Pallet'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
